import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Xmark } from 'iconoir-react';
import { useTheme } from 'components/core/theme-provider';
import { Sheet, SheetClose, SheetContent, SheetTitle } from 'components/ui/sheet';
import { cn } from 'lib/utils';

type SettingsDrawerProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  /** Shown beside the title, e.g. a view-only badge. */
  badge?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  className?: string;
};

type SettingsGroupProps = {
  children: ReactNode;
  className?: string;
};

type SettingsItemProps = {
  title?: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
};

/** Right-side settings panel. On larger screens it sits below the 64px app header. */
export function SettingsDrawer({
  open,
  onOpenChange,
  title,
  badge,
  children,
  footer,
  className,
}: SettingsDrawerProps) {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const titleColor = theme === 'dark' ? 'text-true-white' : 'text-grey-900';

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        hideClose
        hideOverlay
        aria-describedby={undefined}
        className={cn(
          'bottom-3 left-2 right-2 top-3 flex h-auto w-auto flex-col gap-5 rounded-xl border border-grey-100 bg-true-white px-3 py-6 shadow-none sm:left-auto sm:right-3 sm:top-[76px] sm:w-full sm:max-w-[421px] sm:p-6 dark:border-border dark:bg-card',
          className
        )}
      >
        <div className="flex items-center justify-between gap-4">
          <div className="flex min-w-0 items-center gap-3">
            <SheetTitle className={cn('shrink-0 text-xl font-semibold leading-heading', titleColor)}>
              {title}
            </SheetTitle>
            {badge}
          </div>
          <SheetClose className="rounded-sm text-grey-300 transition-opacity hover:opacity-70 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring">
            <Xmark width={24} height={24} strokeWidth={1.5} />
            <span className="sr-only">{t('CLOSE', { defaultValue: 'Close' })}</span>
          </SheetClose>
        </div>

        <div
          className={cn(
            'flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto overflow-x-hidden pb-4',
            '[scrollbar-width:thin] [scrollbar-color:hsl(var(--grey-200))_transparent]',
            '[&::-webkit-scrollbar]:w-[5px] [&::-webkit-scrollbar-track]:bg-transparent',
            '[&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-grey-200'
          )}
        >
          {children}
        </div>

        {footer ? <div className="flex justify-end gap-2">{footer}</div> : null}
      </SheetContent>
    </Sheet>
  );
}

/** One block of settings, divided from the next by a bottom border. */
export function SettingsGroup({ children, className }: SettingsGroupProps) {
  return (
    <div
      className={cn(
        'flex w-full flex-col gap-4 border-b border-grey-100 pb-6 dark:border-border',
        className
      )}
    >
      {children}
    </div>
  );
}

/**
 * Title and optional hint on the left, an optional control on the right.
 * The control wraps below the text when the panel is too narrow, so wide actions
 * (e.g. Add button + switch) never squeeze the text into one word per line.
 */
export function SettingsItem({ title, description, action }: SettingsItemProps) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  return (
    <div className="flex w-full flex-wrap items-start gap-3">
      <div className="flex min-w-44 flex-1 flex-col gap-1">
        {title ? (
          <p
            className={cn(
              'break-words text-sm font-medium leading-body',
              isDark ? 'text-true-white' : 'text-grey-900'
            )}
          >
            {title}
          </p>
        ) : null}
        {description ? (
          <p
            className={cn(
              'text-xs font-medium leading-body',
              isDark ? 'text-muted-foreground' : 'text-grey-500'
            )}
          >
            {description}
          </p>
        ) : null}
      </div>
      {action ? <div className="shrink-0">{action}</div> : null}
    </div>
  );
}
